/**
 * 鲸鲸表情：客服在工作台点一下就单独发出一条消息，正文是 [鲸鲸:谢谢] 这样的写法。
 * 客户窗口和工作台把只含这一个写法的客服消息显示成小图；其他地方（列表摘要、旧版本、APP）照原文显示，也能看懂。
 * 图片是 stickers/ 下的本地图（同 id 有 PNG 时用 PNG），按 id 对应；改名或删图会让旧消息退回文字，只能新增（可以换图）。
 * 第一版的 SVG 表情（legacy）不再出现在选择面板里，只用来显示旧消息。
 *
 * 鲸鲸小表情：插进输入框和文字一起发，正文里是 [鲸:微笑] 这样的写法，消息正文按 markdown 渲染时显示成跟文字一样高的小图。
 * 图片是 emotes/ 下的 PNG，规则同上。
 */

const files = import.meta.glob('./stickers/*.{png,svg}', { eager: true, import: 'default', query: '?url' }) as Record<string, string>;
const emoteFiles = import.meta.glob('./emotes/*.png', { eager: true, import: 'default', query: '?url' }) as Record<string, string>;

export interface Sticker {
  id: string;
  label: string;
  url: string;
}

const CATALOG: [id: string, label: string][] = [
  ['hello', '你好'], ['welcome', '不客气'], ['noproblem', '没问题'], ['onit', '马上安排'],
  ['done', '搞定啦'], ['like', '给你点赞'], ['thankyou', '辛苦啦'], ['fivestar', '求五星好评'],
  ['gotit', '收到明白'], ['awesome', '太棒了'], ['goodluck', '一切顺利'], ['morning', '早上好'],
  ['bye', '拜拜啦'], ['patience', '感谢耐心'], ['goforit', '冲鸭'], ['congrats', '恭喜恭喜'],
];

/** 第一版的表情：旧消息里还会出现，照常显示，但不再给人选。 */
const LEGACY: [id: string, label: string][] = [
  ['thanks', '谢谢'], ['ok', '好的'], ['received', '收到'],
  ['wait', '稍等'], ['sorry', '抱歉'], ['cheer', '加油'], ['happy', '开心'],
  ['laugh', '哈哈'], ['heart', '比心'], ['question', '疑问'], ['think', '思考'],
  ['shy', '害羞'], ['cry', '哭哭'], ['surprised', '惊讶'], ['hongbao', '恭喜'],
];

function stickerUrl(id: string): string {
  return files[`./stickers/${id}.png`] ?? files[`./stickers/${id}.svg`] ?? '';
}

function toStickers(list: [id: string, label: string][]): Sticker[] {
  return list.map(([id, label]) => ({ id, label, url: stickerUrl(id) })).filter((item) => item.url !== '');
}

/** 选择面板里的表情。 */
export const STICKERS: Sticker[] = toStickers(CATALOG);

const BY_LABEL = new Map([...STICKERS, ...toStickers(LEGACY)].map((item) => [item.label, item]));
const TOKEN = /^\[鲸鲸:([^\]\n]{1,8})\]$/u;

/** 发出去的消息正文。 */
export function stickerText(sticker: Sticker): string {
  return `[鲸鲸:${sticker.label}]`;
}

/** 整条消息只有一个表情写法时返回该表情，否则为 null（按普通文字显示）。 */
export function stickerOf(text: null | string | undefined): null | Sticker {
  const match = TOKEN.exec((text ?? '').trim());
  return match ? BY_LABEL.get(match[1]!) ?? null : null;
}

const EMOTE_CATALOG: [id: string, label: string][] = [
  ['smile', '微笑'], ['hehe', '嘿嘿'], ['haha', '哈哈'], ['stareyes', '星星眼'], ['love', '喜欢'], ['wink', '眨眼'],
  ['cool', '酷'], ['proud', '得意'], ['shy', '害羞'], ['expect', '期待'], ['yay', '好耶'], ['thumb', '点赞'],
  ['heart', '比心'], ['clap', '鼓掌'], ['ok', 'OK'], ['fist', '抱拳'], ['fighting', '加油'], ['idea', '有主意'],
  ['check', '对'], ['flower', '送花'], ['sun', '好天气'], ['party', '庆祝'], ['handshake', '握手'], ['hongbao', '红包'],
];

export const EMOTES: Sticker[] = EMOTE_CATALOG
  .map(([id, label]) => ({ id, label, url: emoteFiles[`./emotes/${id}.png`] ?? '' }))
  .filter((item) => item.url !== '');

const EMOTE_BY_LABEL = new Map(EMOTES.map((item) => [item.label, item]));
const EMOTE_TOKEN = /^\[鲸:([^\]\n]{1,6})\]/u;

/** 插进输入框的写法。 */
export function emoteText(emote: Sticker): string {
  return `[鲸:${emote.label}]`;
}

/** text 从开头起是一个已知小表情写法时返回该表情和写法长度，供 markdown 行内规则逐处识别。 */
export function emoteAt(text: string): null | { emote: Sticker; length: number } {
  const match = EMOTE_TOKEN.exec(text);
  const emote = match ? EMOTE_BY_LABEL.get(match[1]!) : undefined;
  return match && emote ? { emote, length: match[0].length } : null;
}

/** 常用 emoji，插进输入框里与文字一起发。 */
export const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😋',
  '😎', '🤗', '🤔', '🤨', '😐', '😅', '😓', '😥', '😢', '😭', '😤', '😱',
  '😳', '🥺', '😴', '🙏', '👍', '👌', '👏', '🙌', '💪', '🤝', '✌️', '👋',
  '❤️', '💖', '🌹', '🎉', '🎁', '🧧', '💰', '✅', '❌', '⭕', '❗', '❓',
];
